/**
 * NameModal.js — the app's own text entry.
 *
 * The last two moments the app left the workspace to ask something were
 * window.prompt(): "Save a copy…" and "Rename scenario". A native prompt is a
 * browser strip — no translation of the button, no room to explain what the
 * name is for, and on a phone the field is off-screen while the keyboard opens.
 *
 * Same chrome as ConfirmModal, with a field that arrives focused and selected,
 * so the common case is: press Save As → type the new name → Enter.
 *
 * Props:
 * - title, label:   strings
 * - defaultValue:   string   the field opens with it selected
 * - confirmLabel:   string   the verb (Save copy / Rename)
 * - onSubmit:       (name: string) => void   only called with a non-empty trimmed name
 * - onCancel:       () => void
 */
import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from './i18n';

export default function NameModal({ title = '', label = '', defaultValue = '', confirmLabel = 'Save', onSubmit, onCancel }) {
  const { t } = useI18n();
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select(); // the usual edit is "change the whole name" — start selected
  }, []);

  // Esc cancels, exactly like ConfirmModal — the two dialogs must behave alike.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel && onCancel(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const submit = () => {
    const name = value.trim();
    if (!name) return; // an empty name is not an answer; keep asking
    onSubmit && onSubmit(name);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onCancel && onCancel();
      }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10050, background: 'rgba(2, 6, 23, 0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div style={{
        width: 'min(420px, 100%)', background: '#0f172a', border: '1px solid #334155',
        borderRadius: 12, padding: '1rem 1.1rem', color: '#e2e8f0',
        boxShadow: '0 18px 44px rgba(0,0,0,0.6)',
      }}>
        <h2 style={{ margin: '0 0 8px', fontSize: '1rem' }}>{title}</h2>
        {label ? (
          <div style={{ fontSize: '0.76rem', color: '#94a3b8', margin: '0 0 6px' }}>{label}</div>
        ) : null}
        <form
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              // Enter commits — the whole point of an in-app field is that the
              // answer is one line, not a click on a button below it.
              if (e.key === 'Enter') { e.preventDefault(); submit(); }
            }}
            aria-label={label || title}
            style={{
              width: '100%', boxSizing: 'border-box', minHeight: 42, padding: '0 12px',
              borderRadius: 8, border: '1px solid #334155', background: '#020617',
              color: '#e2e8f0', fontSize: '0.95rem',
            }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button
              type="button"
              style={{
                minWidth: 120, minHeight: 40, padding: '0 14px', borderRadius: 8, cursor: 'pointer',
                fontSize: '0.86rem', fontWeight: 700, border: '1px solid transparent',
                background: '#334155', color: '#fff',
              }}
              onClick={() => onCancel && onCancel()}
            >{t('confirm.cancel')}</button>
            <button
              type="submit"
              disabled={!value.trim()}
              style={{
                minWidth: 120, minHeight: 40, padding: '0 14px', borderRadius: 8, cursor: 'pointer',
                fontSize: '0.86rem', fontWeight: 700, border: '1px solid transparent',
                background: '#4f46e5', color: '#fff', opacity: value.trim() ? 1 : 0.4,
              }}
            >{confirmLabel}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
